import useTableFilters from "@/hooks/useTableFilters";
import { EMPLOYEE_FILTERS } from "components/employee/constants";
import SelectStatus from "components/select/status";
import { FilterActions } from "components/filter/actions";
import FilterWrapper from "components/filter/wrapper";
import FiltersList from "components/filter/list";
import UserCombobox from "components/directus/combobox/user";
import ProjectCombobox from "components/directus/combobox/project";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "ui/components/select";

// "Has project" options mapped to the Directus operator on `project`.
const PROJECT_LINK = {
  with: { label: "Has project", filter: { _nnull: true } },
  without: { label: "No project", filter: { _null: true } },
  all: { label: "All repositories", filter: null },
};
export const DEFAULT_FILTERS = { project: PROJECT_LINK.with.filter };

// Project / status / coordinator are nested under `project` (project.id, project.status, project.coordinator).
const nestedProjectKeys = (filters) => Object.keys(filters?.project || {}).filter((key) => !key.startsWith("_"));

const projectLinkKey = (filters) => {
  // A project, status or coordinator filter only matches repositories that have a project.
  if (filters?.project?._nnull || nestedProjectKeys(filters).length) return "with";
  if (filters?.project?._null) return "without";
  return "all";
};

// Merges nested project filters. Picking any implies "Has project", so _null/_nnull is dropped;
// clearing the last one goes back to "Has project".
const withProjectFilter = (filters, changes) => {
  const hadNested = nestedProjectKeys(filters).length > 0;
  const { _null, _nnull, ...project } = { ...filters?.project, ...changes };
  Object.keys(project).forEach((key) => project[key] === undefined && delete project[key]);
  const operator = hadNested ? { _nnull: true } : { ...(_null && { _null }), ...(_nnull && { _nnull }) };
  const nextProject = Object.keys(project).length ? project : operator;
  const { project: _, ...rest } = filters || {};
  return Object.keys(nextProject).length ? { ...rest, project: nextProject } : rest;
};

// "No project" / "All repositories" clear the nested filters; "Has project" keeps them.
const withProjectLink = (filters, key) => {
  if (key === "with" && nestedProjectKeys(filters).length) return filters;
  const { project: _, ...rest } = filters || {};
  const filter = PROJECT_LINK[key].filter;
  return filter ? { ...rest, project: filter } : rest;
};

export default function GithubRepoFilters({ filters, setFilters, setIsOpen, isOpen, defaultFilters, clearFilters }) {
  const { localFilters, setLocalFilters, applyFilters, clearLocalFilters } = useTableFilters({
    filters,
    setFilters,
    setIsOpen,
    clearFilters,
    initialFilters: { ...defaultFilters },
  });
  const project = localFilters?.project || {};
  const update = (changes) => setLocalFilters((prev) => withProjectFilter(prev, changes));

  return (
    <FiltersList>
      <FilterWrapper label="Repositories">
        <Select value={projectLinkKey(localFilters)} onValueChange={(key) => setLocalFilters((prev) => withProjectLink(prev, key))}>
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(PROJECT_LINK).map(([key, { label }]) => (
              <SelectItem key={key} value={key}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FilterWrapper>
      <FilterWrapper label="Project">
        <ProjectCombobox
          value={project.id?._eq}
          className="w-full"
          placeholder="Search and Select"
          searchPlaceholder="Search Project..."
          dependsOn={isOpen}
          onChange={(selectedItem) => update({ id: selectedItem ? { _eq: selectedItem } : undefined })}
          hideLabel
        />
      </FilterWrapper>
      <FilterWrapper label="Project Status">
        <SelectStatus
          collection="projects"
          value={project.status?._eq}
          onChange={(selectedItem) => update({ status: selectedItem ? { _eq: selectedItem } : undefined })}
        />
      </FilterWrapper>
      <UserCombobox
        label="Project Coordinator"
        value={project.coordinator?._eq}
        onChange={(selectedItem) => update({ coordinator: selectedItem ? { _eq: selectedItem } : undefined })}
        filter={EMPLOYEE_FILTERS.projects_coordinating}
        dependenton={isOpen}
      />
      <FilterActions applyFilters={applyFilters} clearLocalFilters={clearLocalFilters} />
    </FiltersList>
  );
}
